/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import {
    Heading,
    TextField,
    Button,
    PasswordRequirements,
    passwordValidator,
    FormValidationAlert,
    type FormValidationAlertErrors,
    useFlags,
} from 'sarsaparilla';
import { useFieldRefValidation, getMessageFromError } from 'sarsaparilla/src/utils';
import {
    postApplyNewPassword,
    postApplyNewPasswordErrors,
    postLoginV2,
} from 'shared-ui/api/account';
import { useRecaptchaWithFallback } from 'shared-ui/recaptcha';

import { matchPasswordValidator } from 'shared-ui/utils/validators';
import { useAuthModalScreen } from '../AuthModalScreenContext';

import {
    EmailCodeAndNewPasswordErrors,
    type EmailCodeAndNewPasswordErrorType,
} from './EmailCodeAndNewPasswordErrors';

import styles from './EmailCodeAndNewPasswordForm.module.scss';

interface EmailCodeAndNewPasswordFormProps {
    emailAddress: string;
    onResendEmailClick: () => void;
    transitionToMfaCodeScreen: () => void;
    transitionToSuccessScreen: () => void;
}

export const RESEND_WAIT_SECONDS = 60;

export function EmailCodeAndNewPasswordForm({
    emailAddress,
    onResendEmailClick,
    transitionToMfaCodeScreen,
    transitionToSuccessScreen,
}: EmailCodeAndNewPasswordFormProps) {
    const { accountLoginRecaptcha } = useFlags();
    const isRecaptchaEnabled = accountLoginRecaptcha === true;

    const { storeUserData, closeModal } = useAuthModalScreen();
    const [isLoading, setIsLoading] = React.useState(false);
    const [password, setPassword] = React.useState('');
    const [errorType, setErrorType] =
        React.useState<EmailCodeAndNewPasswordErrorType>(null);
    const [validationErrors, setValidationErrors] =
        React.useState<FormValidationAlertErrors | null>(null);

    const { getToken, getRecaptchaRegion } = useRecaptchaWithFallback();

    const { addFieldRef, validate } = useFieldRefValidation();

    const [secondsUntilResend, setSecondsUntilResend] =
        React.useState(RESEND_WAIT_SECONDS);

    React.useEffect(() => {
        if (secondsUntilResend <= 0) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setSecondsUntilResend((remainingSeconds) => remainingSeconds - 1);
        }, 1000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [secondsUntilResend]);

    const isResendWaiting = secondsUntilResend > 0;

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setValidationErrors(null);
        setErrorType(null);

        // Get form values
        const formData = new FormData(event.currentTarget);
        const code = formData.get('code')?.toString().trim();

        // Validate form
        const { isValid, errors } = validate();

        // Form is invalid
        if (!isValid || !code || !password) {
            setValidationErrors(errors);
            setIsLoading(false);
            return;
        }

        // Step 1: Save the new password. This is the only step that can surface
        // an inline error on this screen.
        try {
            const { data } = await postApplyNewPassword({
                email: emailAddress,
                token: code,
                password,
            });

            if (data.mfa_login) {
                transitionToMfaCodeScreen();
                setIsLoading(false);
                return;
            }
        } catch (error) {
            const errorMessage = getMessageFromError(error);

            if (errorMessage === postApplyNewPasswordErrors.FAILED_TO_RESET) {
                setErrorType('verifyFailed');
            } else if (errorMessage === postApplyNewPasswordErrors.LOCKOUT) {
                setErrorType('lockout');
            } else {
                setErrorType('unknown');
            }
            setIsLoading(false);
            return;
        }

        // Step 2: Best-effort auto-login. The password is already saved, so any
        // failure (including a reCAPTCHA challenge) hands off to the success
        // screen -> login screen rather than showing a scary error here.
        try {
            const recaptchaToken = isRecaptchaEnabled
                ? await getToken('login')
                : undefined;
            const recaptchaType = isRecaptchaEnabled ? getRecaptchaRegion() : undefined;

            if (isRecaptchaEnabled && !recaptchaToken) {
                transitionToSuccessScreen();
                return;
            }

            const { data: loginData } = await postLoginV2({
                username: emailAddress,
                password,
                recaptchaToken,
                recaptchaType,
            });

            if (loginData.mfa_login) {
                transitionToMfaCodeScreen();
                return;
            }

            // User is logged in - store user data in redux and local storage
            storeUserData(loginData);
            closeModal();
        } catch (error) {
            transitionToSuccessScreen();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="p-sm-3">
            <Heading headingLevel={1} appearance="h4" id="modal-heading">
                Check Your Email
            </Heading>

            <div className="text-md mt-2 mt-4">
                <p className="text-lg font-weight-semibold">
                    If you have an account with us, a verification code will be sent to{' '}
                    <span className={styles.emailAddress}>{emailAddress}</span>.
                </p>

                <p className="text-md mt-1">
                    Please enter the code to confirm your account. If you do not receive
                    an email to reset your password, please check your spam folder or
                    select <strong className="font-weight-semibold">Resend Email</strong>{' '}
                    below.
                </p>
            </div>

            <form onSubmit={onSubmit} className="mt-4">
                <FormValidationAlert errors={validationErrors} className="mb-4" />

                <EmailCodeAndNewPasswordErrors errorType={errorType} className="mb-4" />

                <TextField
                    label="Verification Code"
                    id="code"
                    name="code"
                    isRequired
                    ref={addFieldRef(0)}
                />

                <TextField
                    label="New Password"
                    type="password"
                    id="password"
                    name="password"
                    value={password}
                    onChange={(event) => setPassword(event.currentTarget.value)}
                    validator={passwordValidator}
                    isRequired
                    enableShowPassword
                    ref={addFieldRef(1)}
                    className="mt-4"
                />

                <PasswordRequirements password={password} className="mt-1" />

                <TextField
                    label="Re-Enter New Password"
                    type="password"
                    id="passwordRepeat"
                    name="passwordRepeat"
                    className="mt-4"
                    isRequired
                    enableShowPassword
                    validator={matchPasswordValidator(password)}
                    ref={addFieldRef(2)}
                />

                <Button
                    type="submit"
                    size="lg"
                    appearance="primary"
                    shouldFitContainer
                    className="mt-4"
                    isLoading={isLoading}
                >
                    Save Password
                </Button>
                <Button
                    type="button"
                    appearance="subtle"
                    shouldFitContainer
                    onClick={onResendEmailClick}
                    isDisabled={isLoading || isResendWaiting}
                    className="mt-1"
                >
                    Resend Email
                    {isResendWaiting && (
                        <span className="ml-half" aria-hidden="true">
                            ({secondsUntilResend}s)
                        </span>
                    )}
                </Button>

                <div className="rec-sr-only" role="status">
                    {isResendWaiting
                        ? `Resend Email will be available in ${RESEND_WAIT_SECONDS} seconds.`
                        : 'Resend Email is now available.'}
                </div>
            </form>
        </div>
    );
}
